import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/inbox")({
  component: InboxPage,
});

export function InboxPage() {
  const { data: unreadCount } = useQuery(convexQuery(api.notifications.getUnreadCount, {}));
  const { data: inbox } = useQuery(convexQuery(api.notifications.listInbox, {
    paginationOpts: { numItems: 100, cursor: null },
  }));
  const markRead = useMutation(api.notifications.markRead);

  const handleMarkRead = async (notificationId: Id<"notifications">) => {
    try {
      await markRead({ notificationId });
    } catch (error) {
      console.error(error);
      toast.error("Failed to mark notification as read");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inbox"
        description="Notifications across tools"
        breadcrumbs={[{ label: "Inbox" }]}
        actions={
          <Badge variant={unreadCount && unreadCount > 0 ? "secondary" : "outline"}>
            {(unreadCount ?? 0).toString()} unread
          </Badge>
        }
      />

      {inbox === undefined ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">Loading inbox...</CardContent>
        </Card>
      ) : inbox.page.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            No notifications yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {inbox.page.map((row) => {
            const payload = row.payload as
              | {
                  revisionId?: Id<"projectDocumentRevisions">;
                  preview?: string;
                }
              | undefined;

            return (
              <Card key={row._id} className={row.isRead ? "opacity-70" : ""}>
                <CardContent className="flex items-start justify-between gap-3 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Bell className="size-4 text-muted-foreground" />
                      <p className="text-sm font-medium">
                        {row.type === "revcollab_mention"
                          ? `${row.actor?.name ?? "Someone"} mentioned you`
                          : row.type}
                      </p>
                      {!row.isRead ? <Badge variant="secondary">Unread</Badge> : null}
                    </div>
                    {payload?.preview ? (
                      <p className="text-sm text-muted-foreground line-clamp-2">{payload.preview}</p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</p>
                    {payload?.revisionId ? (
                      <AppLink
                        to="/revCollab/revisions/$revisionId"
                        params={{ revisionId: payload.revisionId }}
                        title="CollabPilot session"
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Open CollabPilot thread
                      </AppLink>
                    ) : null}
                  </div>
                  {!row.isRead ? (
                    <Button variant="ghost" size="sm" onClick={() => void handleMarkRead(row._id)}>
                      <CheckCheck className="size-4" />
                      Mark read
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
